import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link } from "@/lib/router-compat";

type Review = {
  id: string;
  rating: number;
  title: string | null;
  body: string;
  created_at: string;
  status: string;
  customer_id: string;
};

export function StarRating({
  value,
  size = 16,
  interactive = false,
  onChange,
}: {
  value: number;
  size?: number;
  interactive?: boolean;
  onChange?: (n: number) => void;
}) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div className="inline-flex items-center gap-0.5" role={interactive ? "radiogroup" : "img"} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(display);
        const StarEl = (
          <Star
            className={filled ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}
            style={{ width: size, height: size }}
          />
        );
        if (!interactive) return <span key={n}>{StarEl}</span>;
        return (
          <button
            type="button"
            key={n}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange?.(n)}
            className="p-0.5 hover:scale-110 transition-transform"
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            {StarEl}
          </button>
        );
      })}
    </div>
  );
}

export function ReviewSummary({
  average,
  count,
  onClick,
}: {
  average: number;
  count: number;
  onClick?: () => void;
}) {
  if (!count) return null;
  const content = (
    <span className="inline-flex items-center gap-2 text-sm">
      <StarRating value={average} size={16} />
      <span className="font-medium">{average.toFixed(1)}</span>
      <span className="text-muted-foreground">
        ({count} review{count === 1 ? "" : "s"})
      </span>
    </span>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className="hover:underline">
      {content}
    </button>
  ) : (
    content
  );
}

export default function ProductReviews({
  productSlug,
  productTitle,
  onSummaryChange,
}: {
  productSlug: string;
  productTitle: string;
  onSummaryChange?: (s: { average: number; count: number }) => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!productSlug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("product_reviews")
        .select("id,rating,title,body,created_at,status,customer_id")
        .eq("product_slug", productSlug)
        .eq("status", "approved")
        .order("created_at", { ascending: false })
        .limit(50);
      if (!cancelled) {
        setReviews((data as Review[]) ?? []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productSlug]);

  useEffect(() => {
    if (!user) {
      setCustomerId(null);
      setMyReview(null);
      return;
    }
    (async () => {
      const { data: c } = await supabase
        .from("parts_customers")
        .select("id")
        .eq("auth_user_id", user.id)
        .maybeSingle();
      const cid = (c as { id: string } | null)?.id ?? null;
      setCustomerId(cid);
      if (cid) {
        const { data: mine } = await supabase
          .from("product_reviews")
          .select("id,rating,title,body,created_at,status,customer_id")
          .eq("product_slug", productSlug)
          .eq("customer_id", cid)
          .maybeSingle();
        setMyReview((mine as Review | null) ?? null);
        if (mine) {
          const m = mine as Review;
          setRating(m.rating);
          setTitle(m.title ?? "");
          setBody(m.body);
        }
      }
    })();
  }, [user, productSlug]);

  const summary = useMemo(() => {
    if (reviews.length === 0) return { average: 0, count: 0 };
    const avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
    return { average: Math.round(avg * 100) / 100, count: reviews.length };
  }, [reviews]);

  useEffect(() => {
    onSummaryChange?.(summary);
  }, [summary, onSummaryChange]);

  const canSubmit = !!user && !!customerId;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customerId) return;
    if (!body.trim() || body.trim().length < 10) {
      toast({ title: "Please write a bit more", description: "Reviews must be at least 10 characters.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const payload = {
      product_slug: productSlug,
      customer_id: customerId,
      rating,
      title: title.trim() || null,
      body: body.trim(),
      status: "pending" as const,
    };
    const { error } = myReview
      ? await supabase.from("product_reviews").update({ rating, title: payload.title, body: payload.body, status: "pending" }).eq("id", myReview.id)
      : await supabase.from("product_reviews").insert(payload);
    setSubmitting(false);
    if (error) {
      toast({ title: "Couldn't submit review", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Thanks for your review!", description: "It will appear after moderation." });
    setShowForm(false);
    if (!myReview) {
      setMyReview({
        id: "pending",
        rating,
        title: payload.title,
        body: payload.body,
        created_at: new Date().toISOString(),
        status: "pending",
        customer_id: customerId,
      });
    } else {
      setMyReview({ ...myReview, rating, title: payload.title, body: payload.body, status: "pending" });
    }
  }

  return (
    <section id="reviews" className="mt-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold">Customer Reviews</h2>
          {summary.count > 0 ? (
            <div className="mt-1">
              <ReviewSummary average={summary.average} count={summary.count} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">Be the first to review {productTitle}.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!user && (
            <Button asChild size="sm">
              <Link to={`/parts-catalog/account/login?redirect=${encodeURIComponent(window.location.pathname + "#reviews")}`}>
                Sign in to review
              </Link>
            </Button>
          )}
          {user && !customerId && (
            <p className="text-xs text-muted-foreground">
              Only registered parts customers can leave a review.
            </p>
          )}
          {canSubmit && (
            <Button size="sm" variant={showForm ? "outline" : "default"} onClick={() => setShowForm((v) => !v)}>
              {showForm ? "Cancel" : myReview ? "Edit your review" : "Write a review"}
            </Button>
          )}
        </div>
      </div>

      {myReview && !showForm && (
        <div className="mb-4 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-primary uppercase tracking-wide mb-1">
            Your review
            <span className="text-muted-foreground normal-case tracking-normal">
              · {myReview.status === "approved" ? "Published" : myReview.status === "pending" ? "Awaiting moderation" : "Not approved"}
            </span>
          </div>
          <StarRating value={myReview.rating} />
          {myReview.title && <div className="font-semibold mt-1">{myReview.title}</div>}
          <p className="text-sm mt-1 whitespace-pre-line">{myReview.body}</p>
        </div>
      )}

      {canSubmit && showForm && (
        <form onSubmit={handleSubmit} className="mb-6 rounded-lg border bg-card p-4 space-y-3">
          <div>
            <label className="text-sm font-medium">Your rating</label>
            <div className="mt-1">
              <StarRating value={rating} size={26} interactive onChange={setRating} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Title (optional)</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Sums it up in a few words" />
          </div>
          <div>
            <label className="text-sm font-medium">Your review</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={4000} placeholder="Share what you liked or didn't like about this product" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {myReview ? "Update review" : "Submit review"}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading reviews…</div>
      ) : reviews.length === 0 ? null : (
        <ul className="space-y-4">
          {reviews.map((r) => (
            <li key={r.id} className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2">
                <StarRating value={r.rating} />
                <span className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
              </div>
              {r.title && <div className="font-semibold mt-1">{r.title}</div>}
              <p className="text-sm mt-1 whitespace-pre-line">{r.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}